import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Grid, Typography } from "@mui/material";
import GamesIcon from "@mui/icons-material/Games";

import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";

import httpclient from "../../../Utils";
import CustomLoadingOverlay from "../../../Components/CustomLoadingOverlay";

import { getColumns } from "./columns";
import StatusChangeConfirmationModal from "./StatusChangeConfirmationModal";
import BreakReasonModal from "./BreakReasonModal"; // New modal component
import { useBreakReason } from "../../../Hooks/useBreakReason"; 

export default function BreakReason() {
  const [selectedReason, setSelectedReason] = useState(null);
  const [changeStatus, setChangeStatus] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([]);
  const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);
  const [openBreakReasonModal, setOpenBreakReasonModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' or 'edit'
const [allowAdd, setAllowAdd] = useState(1);
  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const { breakReasons, loading, rowCount, refreshBreakReasons } = useBreakReason({
    pagination: paginationModel,
    sorting: sortModel,
  });

//   const handleChangeStatus = async () => {
//     setChangeStatus(true);
//     const API_URL = `/break-reason/status/${selectedReason.id}`; // Updated endpoint

//     try {
//       const { data } = await httpclient.post(API_URL);

//       if (data.status === 200) {
//         showNotification("success", data.message);
//         setSelectedReason(null);
//         setOpenStatusChangeModal(false);
//         refreshBreakReasons();
//       } else {
//         showNotification("error", data.message);
//       }
//     } catch (error) {
//       console.log(error, "error changing status");
//       handleApiError(error);
//     } finally {
//       setChangeStatus(false);
//     }
//   };

const handleChangeStatus = async () => {
  setChangeStatus(true);
  
  // Determine the new status (toggle from current)
  const newStatus = selectedReason.status === 1 ? 0 : 1;
  
  // API endpoint - try different variations if needed
  const API_URL = `/break-reason/status/${selectedReason.id}`;
  // OR if the above doesn't work, try:
  // const API_URL = `/break-reason/update/${selectedReason.id}?checkError=1`;

  try {
    // Send POST request with the new status in payload
    const { data } = await httpclient.post(API_URL, {
      status: newStatus
    });

    if (data.status === 200) {
      showNotification("success", data.message);
      setSelectedReason(null);
      setOpenStatusChangeModal(false);
       if (data.allow_add !== undefined) {
          setAllowAdd(data.allow_add);
        }
      refreshBreakReasons();
    } else {
      showNotification("error", data.message);
    }
  } catch (error) {
    console.log("Error changing status:", error);
    console.log("Error response:", error.response?.data);
    handleApiError(error);
  } finally {
    setChangeStatus(false);
  }
};

  const handleOpenAddModal = () => {
    setModalMode("add");
    setSelectedReason(null);
    setOpenBreakReasonModal(true);
  };

  const handleOpenEditModal = (reason) => {
    setModalMode("edit");
    setSelectedReason(reason);
    setOpenBreakReasonModal(true);
  };

  const handleCloseModal = () => {
    setOpenBreakReasonModal(false);
    setSelectedReason(null);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = getColumns(
    setSelectedReason,
    setOpenStatusChangeModal,
    handleOpenEditModal // Pass the edit modal opener instead of navigate
  );

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#fff",
          padding: "24px 16px",
          borderRadius: "8px",
        }}
      >
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          mb={2}
        >
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Break Reasons
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            {allowAdd === 1 && (
            <Button
              variant="contained"
              sx={{
                borderRadius: "4px",
                backgroundColor: "#7152F3",
                "&:hover": {
                  backgroundColor: "#5c34fa",
                },
              }}
              startIcon={<GamesIcon />}
              onClick={handleOpenAddModal} // Open modal instead of navigate
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                }}
              >
                Add New Break Reason
              </Typography>
            </Button>
            )}
          </Grid>
        </Grid>

        <DataGrid
          rows={breakReasons || []}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          loading={loading}
          slots={{
            loadingOverlay: CustomLoadingOverlay,
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "14px",
              fontWeight: 500,
              color: "#48505E",
            },
          }}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
        />
        
        {openStatusChangeModal && (
          <StatusChangeConfirmationModal
            open={openStatusChangeModal}
            cancel={() => setOpenStatusChangeModal(false)}
            title="Change Status"
            item={selectedReason?.reason_name}
            loading={changeStatus}
            handleChangeStatus={handleChangeStatus}
          />
        )}

        {openBreakReasonModal && (
          <BreakReasonModal
            open={openBreakReasonModal}
            onClose={handleCloseModal}
            mode={modalMode}
            reasonData={selectedReason}
            onSuccess={refreshBreakReasons}
          />
        )}
      </Box>
    </Box>
  );
}